import useCleanTrackingParams from '../../hooks/useCleanTrackingParams';
import { isVotingRunPath } from '../../lib/votings';
import { useLayout } from '../../providers/LayoutProvider';
import { useResponsive } from '../../providers/ResponsiveProvider';
import { __ } from '../../utils';
import AboutButton from '../Header/AboutButton';
import HeaderUser from '../Header/HeaderUser';
import ShareButton from '../Header/ShareButton';
import Pulldown from '../Navigation/Pulldown';
import { useNavItems } from '../Navigation/useNavItems';
import Notifications from '../Notifications/Notifications';
import SearchBar from '../SearchBar';
import MessagesBanner from './MessagesBanner';
import { useAuth } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  Erb,
  Layout as Shell,
  PrihlasenieIcon,
  Stack,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';

/**
 * Shell aplikácie. Celý ho drží `Layout` z knižnice - hlavička, bočná
 * navigácia, spodná lišta na mobile, hlavička stránky aj pripnutá pätička.
 * Tento súbor je len prepojenie appky naň.
 *
 * Čo zostáva tu a prečo:
 *
 * - Používateľa kreslí `UserMenu` z knižnice, obalený v `HeaderUser` - rovnako
 *   ako v cimpe. Gamifikácia (prstenec úrovne, body) zostala profilu: v hlavičke
 *   z nej bol drobný krúžok s číslom, ktorý nikto nečítal. `ShareButton`
 *   a `Notifications` sú naďalej slotom appky, tie knižnica nemá čím nahradiť.
 * - Pás nad hlavičkou drží `MessagesBanner`: správy platformy zadané v cimpe,
 *   doručené REST-om a MQTT pingom. Predtým tu bol natvrdo napísaný text
 *   o testovacom režime; zadáva sa teraz v cimpe ako každá iná správa.
 * - Pull-to-refresh potrebuje ten skrolovací kontejner, ktorý vlastní shell -
 *   preto si ho vyžiada cez `contentRef`. Logika zostáva v appke; plocha
 *   s vlastným zvislým gestom potrebuje `data-pulldown-blocked='true'`
 *   (`.docs/vyvoj/shell.md`).
 * - `@media print` šírka je na obale: shell je `width: 100%`, takže ju preberie.
 */
const Layout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isNotMd } = useResponsive();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navigation = useNavItems();
  const { authUser, generateLoginUrl } = useAuth();
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Clean tracking parameters from URL (fbclid, gclid, etc.)
  useCleanTrackingParams();

  const {
    pageTitle,
    backPath,
    isRootPage,
    actions,
    tabs,
    selectedTab,
    setSelectedTab,
    isLoadingTabs,
    breadcrumbs,
    withoutLayoutPadding,
    footer,
    aside,
    pageFilters,
    pageMeta,
    searchBarParams,
    searchValue,
    setSearchValue,
  } = useLayout();

  const activeTab = Math.max(
    0,
    tabs.findIndex((tab) => tab.value === (selectedTab ?? tabs[0]?.value)),
  );

  const pageActions = (
    <>
      {!!actions?.length && (
        <Stack
          className='screen-only'
          direction='row'
          gap={1}
          flexWrap='wrap'
          sx={{ flexShrink: 0 }}
        >
          {actions.map((action, index) =>
            action.children ? (
              <React.Fragment key={index}>{action.children}</React.Fragment>
            ) : (
              <Button
                key={index}
                variant={action.variant || 'outlined'}
                color={action.color || 'primary'}
                size='small'
                endIcon={action.icon}
                onClick={action.onClick}
                disabled={action.disabled}
                loading={action.loading}
              >
                {action.label}
              </Button>
            ),
          )}
        </Stack>
      )}
      {!!searchBarParams && (
        <SearchBar
          {...searchBarParams}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
      )}
    </>
  );

  // Sústredený režim hlasovania stojí **mimo shellu**: kým je otvorený lístok,
  // nemá byť kam odbočiť. Rozhoduje o tom čistá funkcia nad `pathname`, nie
  // stav v `LayoutProvider`-i - ten sa plní v efekte, takže by shell na prvý
  // render ešte stihol nakresliť hlavičku a stránka by poskočila. Stránka si
  // za to musí sama nainštalovať `LinkProvider` (viď `.docs/vyvoj/shell.md`).
  if (isVotingRunPath(pathname)) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ '@media print': { width: '210mm !important' } }}>
      {isNotMd && (
        <Pulldown
          target={contentRef}
          onPull={() => new Promise(() => window.location.reload())}
        />
      )}

      <Shell
        variant={isNotMd ? 'mobile' : 'desktop'}
        // Navigácia je na desktope hore, nie vľavo. Je to prop shellu, takže
        // to platí na každej stránke appky vrátane administrácie - bočná lišta
        // ostáva CIMPU, ktoré prop neposiela.
        navPlacement='top'
        logo={<Erb variant='light' size={30} />}
        title={__('Môj Prešov')}
        // Erb aj titul sú jeden odkaz domov - obaľuje ich shell, nie appka.
        homeHref='/'
        navigation={navigation}
        currentPath={pathname}
        onNavigate={(href) => navigate(href)}
        contentRef={contentRef}
        menuTitle={__('Menu')}
        flush={withoutLayoutPadding}
        footer={footer}
        aside={aside}
        // Správy platformy zadané v cimpe; keď žiadna nie je, pás sa nekreslí.
        banner={<MessagesBanner />}
        header={{
          right: (
            <Stack direction='row' alignItems='center' gap={0.5}>
              <AboutButton />
              <ShareButton />
              {authUser ? (
                <Stack
                  direction='row'
                  alignItems='center'
                  gap={1}
                  className='screen-only'
                >
                  <Notifications />
                  <HeaderUser />
                </Stack>
              ) : (
                // Biele tlačidlo s rámom, nie tmavočervené: na červenej
                // hlavičke bolo tmavočervené na tmavočervenom a nečítalo sa
                // ako akcia. Ikonu si berie len desktop - na mobile by
                // z tlačidla urobila najširší prvok hlavičky.
                <Button
                  className='screen-only'
                  variant='outline'
                  size='small'
                  startIcon={
                    isNotMd ? undefined : <PrihlasenieIcon weight={2} />
                  }
                  onClick={() => {
                    window.location.href = generateLoginUrl();
                  }}
                >
                  {__('Prihlásiť sa')}
                </Button>
              )}
            </Stack>
          ),
        }}
        page={
          pageTitle || backPath
            ? {
                title: pageTitle ?? '',
                meta: pageMeta,
                // Šípku Späť kreslí hlavička sama; koreň sekcie ju vypína.
                root: isRootPage,
                backPath,
                crumbs: breadcrumbs,
                actions: pageActions,
                filters: pageFilters,
                // Kým sa taby dopočítavajú, hlavička ich nekreslí vôbec.
                tabs: isLoadingTabs ? undefined : tabs,
                activeTab,
                onTabChange: (index) => {
                  const tab = tabs[index];
                  if (tab) setSelectedTab(tab.value);
                },
              }
            : undefined
        }
      >
        {children}
      </Shell>
    </Box>
  );
};

export default Layout;
